// src/components/DateBar/DateBar.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import "dayjs/locale/es";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

// Activar los plugins de dayjs
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale("es");

const LIMA_TZ = "America/Lima";

const getTodayIso = () => dayjs().tz(LIMA_TZ).format("YYYY-MM-DD");

const getSafeDate = (date) => {
  if (date && dayjs(date).isValid()) {
    return dayjs(date).tz(LIMA_TZ);
  }

  return dayjs().tz(LIMA_TZ);
};

const DateBar = ({ selectedDate, onDateChange }) => {
  const [dates, setDates] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const dateBarRef = useRef(null);

  const generateDates = useCallback((centerDate) => {
    const safeCenterDate = getSafeDate(centerDate);
    const tempDates = [];
    const startDate = safeCenterDate.subtract(6, "day");

    for (let i = 0; i < 13; i++) {
      tempDates.push(startDate.add(i, "day").format("YYYY-MM-DD"));
    }

    setDates(tempDates);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 640);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Si el día cambia desde el calendario externo, recentra la barra sobre ese día.
  useEffect(() => {
    generateDates(getSafeDate(selectedDate));
  }, [generateDates, selectedDate]);

  useEffect(() => {
    const selectedElement = dateBarRef.current?.querySelector(
      `[data-date="${selectedDate}"]`
    );

    if (selectedElement) {
      selectedElement.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center",
      });
    }
  }, [dates, selectedDate]);

  const handlePrevClick = () => {
    const newCenterDate = getSafeDate(selectedDate).subtract(1, "day");
    onDateChange(newCenterDate.format("YYYY-MM-DD"));
  };

  const handleNextClick = () => {
    const newCenterDate = getSafeDate(selectedDate).add(1, "day");
    onDateChange(newCenterDate.format("YYYY-MM-DD"));
  };

  const displayedDates = useMemo(() => {
    if (!isMobile) return dates;

    const currentDate = getSafeDate(selectedDate);

    return [
      currentDate.subtract(1, "day").format("YYYY-MM-DD"),
      currentDate.format("YYYY-MM-DD"),
      currentDate.add(1, "day").format("YYYY-MM-DD"),
    ];
  }, [dates, isMobile, selectedDate]);

  const todayIso = getTodayIso();

  return (
    <div
      className="mt-5 flex items-center justify-between gap-2 overflow-x-auto rounded-2xl border border-gray-100 bg-white px-3 py-4 shadow-sm sm:px-4"
      ref={dateBarRef}
    >
      <button
        type="button"
        onClick={handlePrevClick}
        className="shrink-0 rounded-full p-2 text-gray-500 transition-colors duration-300 hover:bg-indigo-50 hover:text-indigo-600"
        aria-label="Día anterior"
      >
        <FaChevronLeft size={18} />
      </button>

      <div className="flex min-w-max flex-1 justify-center gap-3 sm:gap-6">
        {displayedDates.map((date) => {
          const isSelected = date === selectedDate;
          const isToday = date === todayIso;

          return (
            <button
              type="button"
              key={date}
              data-date={date}
              onClick={() => onDateChange(date)}
              className={`flex min-w-[72px] flex-col items-center rounded-xl px-3 py-2 text-center transition-all duration-300 ${
                isSelected
                  ? "bg-indigo-600 text-white shadow-md shadow-indigo-100"
                  : "text-gray-500 hover:bg-gray-50 hover:text-gray-800"
              }`}
            >
              <span className="text-xs font-semibold uppercase tracking-wide">
                {dayjs(date).tz(LIMA_TZ).format("ddd")}
              </span>
              <span className="text-base font-bold sm:text-lg">
                {dayjs(date).tz(LIMA_TZ).format("D MMM")}
              </span>
              {isToday && (
                <span
                  className={`mt-1 rounded-full px-2 py-0.5 text-xs font-extrabold ${
                    isSelected ? "bg-white/20 text-white" : "bg-red-50 text-red-500"
                  }`}
                >
                  HOY
                </span>
              )}
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={handleNextClick}
        className="shrink-0 rounded-full p-2 text-gray-500 transition-colors duration-300 hover:bg-indigo-50 hover:text-indigo-600"
        aria-label="Día siguiente"
      >
        <FaChevronRight size={18} />
      </button>
    </div>
  );
};

export default DateBar;
